import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

interface SeoProps {
  title?: string;
  description?: string;
  keywords?: string[];
  canonical?: string;
  noindex?: boolean;
  productData?: {
    name?: string;
    category?: string;
    images?: string[];
    price?: string;
    availability?: string;
  };
}

const Seo: React.FC<SeoProps> = ({
  title = "Scadel Furniture",
  description = "Scadel Furniture - India's leading manufacturer of premium commercial furniture, office chairs, school furniture, and custom furniture solutions. Best quality with modern designs.",
  keywords = [
    "scadel furniture",
    "Scadel Furniture",
    "Scadel",
    "office furniture",
    "commercial furniture",
    "school furniture",
    "hotel furniture",
    "office chairs",
    "furniture manufacturer India",
    "Scadel Furniture India",
    "www.scadelfurniture.in",
    "Scadel office chairs",
    "commercial furniture India",
    "premium office furniture",
    "SCADEL FURNITURE", // Added exact match
  ],
  canonical,
  noindex = false,
  productData,
}) => {
  const location = useLocation();
  const baseUrl = "https://www.scadelfurniture.in";
  const currentCanonical = canonical || `${baseUrl}${location.pathname}`;
  const fullTitle = title.includes("Scadel Furniture")
    ? title
    : `${title} | Scadel Furniture`;

  // Generate structured data based on page type
  const generateStructuredData = () => {
    if (productData) {
      // Product page structured data
      return {
        "@context": "https://schema.org",
        "@type": "Product",
        name: productData.name,
        description: description,
        image: productData.images || [`${baseUrl}/images/scadellogosvg.png`],
        offers: {
          "@type": "Offer",
          price: productData.price,
          priceCurrency: "INR",
          availability:
            productData.availability || "https://schema.org/InStock",
        },
        brand: {
          "@type": "Brand",
          name: "Scadel Furniture",
        },
        category: productData.category,
      };
    }

    // Default organization structured data
    return {
      "@context": "https://schema.org",
      "@type": "FurnitureStore",
      name: "Scadel Furniture",
      alternateName: "Scadel",
      description:
        "Premium commercial furniture manufacturer in India specializing in office furniture, school furniture, and custom commercial furniture solutions.",
      url: "https://www.scadelfurniture.in",
      logo: "https://www.scadelfurniture.in/images/scadellogosvg.png",
      image: "https://www.scadelfurniture.in/images/scadellogosvg.png",
      telephone: "+91-XXXXXXXXXX",
      address: {
        "@type": "PostalAddress",
        streetAddress: "Your Street Address",
        addressLocality: "Pune",
        addressRegion: "Maharashtra",
        postalCode: "411001",
        addressCountry: "IN",
      },
      openingHours: "Mo-Fr 09:00-18:00, Sa 09:00-14:00",
      priceRange: "₹₹",
      sameAs: [],
    };
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords.join(", ")} />

      {/* Canonical URL */}
      <link rel="canonical" href={currentCanonical} />

      {/* Robots */}
      <meta
        name="robots"
        content={
          noindex
            ? "noindex, follow"
            : "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
        }
      />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={productData ? "product" : "website"} />
      <meta property="og:url" content={currentCanonical} />
      <meta
        property="og:image"
        content={`${baseUrl}/images/scadellogosvg.png`}
      />
      <meta property="og:site_name" content="Scadel Furniture" />
      <meta property="og:locale" content="en_IN" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta
        name="twitter:image"
        content={`${baseUrl}/images/scadellogosvg.png`}
      />

      {/* Additional Meta Tags */}
      <meta name="author" content="Scadel Furniture" />
      <meta name="publisher" content="Scadel Furniture" />
      <meta
        name="copyright"
        content={`Scadel Furniture ${new Date().getFullYear()}`}
      />
      <meta name="reply-to" content="contact@scadelfurniture.in" />
      <meta
        name="classification"
        content="Furniture, Commercial Furniture, Office Furniture"
      />
      <meta name="target" content="all" />
      <meta name="coverage" content="Worldwide" />
      <meta name="distribution" content="Global" />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(generateStructuredData())}
      </script>

      {/* Additional Breadcrumb Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: "https://www.scadelfurniture.in",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: fullTitle.includes("Gallery")
                ? "Gallery"
                : fullTitle.includes("Contact")
                ? "Contact"
                : fullTitle.split("|")[0].trim(),
              item: currentCanonical,
            },
          ],
        })}
      </script>
    </Helmet>
  );
};

export default Seo;
